import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { convertLanguage, resolveCampaignLocale } from '@/core/i18n/convertLanguage'
import { applyLocaleAndReload } from '@/core/i18n/localeReload'
import type { CampaignData } from '@/schemas/v2/campaign'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// キャンペーンに応じてデフォルトの言語を選ぶようにするhook
// i18nのライブラリが`i18nextLng`というキーでローカルストレージに保存するようになっている。
// callbackで`{campaign-name}_i18nextLng`というキーでローカルストレージに追加で保存するようにもしている。
// そのため、APIなどの実行に利用されるのは、`{campaign-name}_i18nextLng`だが、実際の切り替えに寄与しているのは`i18nextLng`であることに注意
//
// 流れ
// 1. キャンペーンに応じた言語設定がローカルストレージに存在しない場合（= 初回アクセス）
//    1.1. ブラウザの言語設定を取得し、変換する（キャンペーンが提供していないロケールは同じ言語の提供ロケールへ寄せる）
//    1.2. 変換した言語設定を`{campaign-name}_i18nextLng`に保存する
//    1.3. 変換した言語設定を`i18nextLng`に保存する
//    1.4. 寄せた結果がブラウザ由来の値と異なる場合はリロードする（初回の API 取得はブラウザ由来の値で済んでいるため）
// 2. キャンペーンに応じた言語設定がローカルストレージに存在する場合（= 以前アクセス済み & 言語設定済み）
//    2.1. その言語設定を`i18nextLng`に保存する
// 3. キャンペーンが多言語化対応していない場合
//    3.1. `ja_JP`を`{campaign-name}_i18nextLng`に保存する
//    3.2. `ja_JP`を`i18nextLng`に保存する
/** localStorage が使用不可（Safari プライベートモード等）でも例外で effect を中断させない */
const readStoredLocale = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** @returns 保存できたか。保存できない環境ではリロードしても元のロケールに戻るため、呼び出し側でリロードを避ける */
const persistLocale = (key: string, code: string): boolean => {
  try {
    localStorage.setItem(key, code)
    return true
  } catch {
    return false
  }
}

export const useChangeLanguage = ({ campaign }: { campaign?: CampaignData | null }): { language: string } => {
  const { i18n } = useTranslation()
  const { LOCAL_STORAGE_I18N_KEY } = useCampaignSlug()

  // i18n.changeLanguage と localStorage への書き込みは副作用として useEffect で行う
  useEffect(() => {
    if (!campaign) return

    const hasMultipleLanguages = campaign.language.length > 1
    const isLocalizedCampaign = campaign.isLocalize ?? hasMultipleLanguages

    if (!(isLocalizedCampaign && hasMultipleLanguages)) {
      // 多言語化対応していない場合、強制的に日本語に設定する
      persistLocale(LOCAL_STORAGE_I18N_KEY, 'ja_JP')
      i18n.changeLanguage('ja_JP')
      return
    }

    // 多言語化対応している場合
    const campaignSpecificI18nextLng = readStoredLocale(LOCAL_STORAGE_I18N_KEY)
    if (campaignSpecificI18nextLng) {
      // キャンペーンに応じた多言語の設定がある場合には、そのまま設定
      i18n.changeLanguage(campaignSpecificI18nextLng)
      return
    }

    // キャンペーンに応じた多言語の設定がない場合にはブラウザのデフォルトを設定
    const browserLng = convertLanguage(navigator.language)
    const detectedLng = resolveCampaignLocale(
      browserLng,
      campaign.language.map((l) => l.code),
    )
    const isPersisted = persistLocale(LOCAL_STORAGE_I18N_KEY, detectedLng)

    if (isPersisted && detectedLng !== browserLng) {
      // ここに来るまで localStorage は空だったため、resolveLocale() は browserLng を返しており、
      // キャンペーン取得などライフサイクルの X-Local / queryKey はその値で確定している
      // （useRaundLifeCycle の machine は campaignSlug 以外では再構築されない）。
      // 寄せた結果が異なる場合は保存済みの値でリロードして全 API を取り直す。
      // 保存が済んでいるので次回は campaignSpecificI18nextLng が入り、リロードは繰り返さない
      applyLocaleAndReload(i18n, detectedLng)
      return
    }
    // 寄せが発生しない場合はリロード不要。保存できなかった場合はリロードしても元に戻ってしまうため、
    // どちらも表示のみ切り替える
    i18n.changeLanguage(detectedLng)
  }, [campaign, i18n, LOCAL_STORAGE_I18N_KEY])

  // i18n.language は changeLanguage() 呼び出し後に自動更新されるため、直接返す
  return { language: i18n.language }
}
